import { Main, Models, StatusBadge, DataTable, Pagination, SearchInput } from 'utils/imports.utils';
import AvatarInitials from 'common_components/ui/avatar_initials/avatar_initials.ui';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetState, buildAdminListQuery, formatViewCount, getAdminUserLabel, truncateText } from 'utils/functions.utils';
import { ADMIN_TEXT_MAX_CHARS } from 'utils/constant.utils';
import { AdminUserRow } from 'interfaces/admin.interface';
import { STRINGS } from 'constants/strings.constant';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import './admin_users.screen.scss';

const PAGE_SIZE = 20;

const AdminUsers = () => {
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    users: [] as AdminUserRow[],
    totalDocs: 0,
    page: 1,
    search: '',
    loading: true,
  });

  const fetchUsers = async (page: number, search: string) => {
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      // "role: user" excludes admin accounts -- this list is for managing customers, not admins
      // (admin accounts are created via the invite flow, not listed here).
      const query = buildAdminListQuery({ skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, search, role: 'user' });
      const response: any = await Models.auth.adminListUsers(query);
      setState({
        users: response?.data?.docs || [],
        totalDocs: response?.data?.totalDocs || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch users', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchUsers(state.page, state.search);
    } else {
      navigate('/login');
    }
  }, [state.page, state.search]);

  const columns: IDataTableColumn[] = [
    {
      key: 'name',
      label: 'Name',
      render: (row: AdminUserRow) => (
        <span className="admin_users_name">
          <AvatarInitials name={getAdminUserLabel(row)} />
          <span title={row.username || undefined}>{truncateText(row.username || '—', ADMIN_TEXT_MAX_CHARS)}</span>
          {row.is_blocked && <StatusBadge status="blocked" label={STRINGS.adminActions.blocked} tone="negative" />}
        </span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row: AdminUserRow) => row.email || '—',
    },
    { key: 'project_count', label: 'Projects', align: 'right' },
    {
      key: 'total_views',
      label: 'Total views',
      align: 'right',
      render: (row: AdminUserRow) => formatViewCount(row.total_views),
    },
    {
      key: 'created_at',
      label: 'Joined',
      align: 'right',
      render: (row: AdminUserRow) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : '—',
    },
  ];

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Users" title="Users">
        <div className="admin_users">
          <div className="admin_users_search">
            <SearchInput
              onSearch={(value) => setState({ search: value, page: 1 })}
              placeholder="Search users"
            />
          </div>
          <div className="admin_users_card">
            <DataTable
              columns={columns}
              rows={state.users}
              rowKey="_id"
              loading={state.loading}
              emptyMessage="No users found"
              onRowClick={(row: AdminUserRow) => navigate(`/admin/users/${row._id}`)}
            />
          </div>
          <Pagination
            currentPage={state.page}
            totalPages={Math.ceil(state.totalDocs / PAGE_SIZE)}
            pageSize={PAGE_SIZE}
            totalItems={state.totalDocs}
            onPageChange={(page) => setState({ page })}
          />
        </div>
      </AdminPage>
    </Main>
  );
};

export default AdminUsers;
